import React from "react";
import Link from "next/link";
import Image from "next/image";

import {
  CreditCard,
  LogOut,
  User,
  Bookmark,
  MessagesSquare,
  SlidersHorizontal,
  RefreshCcw,
} from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/shadcn/ui/dropdown-menu"

import { cn } from '@/lib/utils/helpers';
import { useRouter } from 'next/router';
import { useAuth } from '@/components/Auth/AuthProvider';

import { URI_SIGN_IN, URI_ACCOUNT, URI_ACCOUNT_PROFILE, URI_ACCOUNT_SAVED_STORIES, URI_ACCOUNT_DISCUSSIONS, URI_ACCOUNT_PERSONALISATION, URI_ACCOUNT_BILLING, URI_ACCOUNT_SUBSCRIPTIONS } from '@/lib/utils/constants';
import { getUserAvatar, getUserFullName, getInitialNames } from '@/lib/utils/helpers';
import { Avatar, AvatarFallback, AvatarImage } from "@/lib/shadcn/ui/avatar"

import Button from "@/components/Button/Button";
import IconUser from '@/lib/assets/images/user-blank.svg';
import { useAuthDialogStore } from '@/lib/stores/post';
import dynamic from "next/dynamic";

export default function UserNav() {
  const router = useRouter();
  const { user, loading, signOut } = useAuth() || {};
  const { openLoginDialog } = useAuthDialogStore();

  const handleLogout = async () => {
    await signOut();
    router.push(URI_SIGN_IN);
  };

  const classNames = cn(
    'mom-btn',
    'text-black hover:text-black !outline-none',
    'dark:text-white hover:dark:text-white',
    'transition-none',
    '[&>svg]:size-7 [&>svg]:mr-0',
    '[&>img]:size-7 [&>img]:mr-0',
  );

  if (loading || !user) {
    return (
      <Button onClick={() => openLoginDialog()} className={cn(
        classNames,
        'mom-btn--icon',
      )} aria-label="Login" title="Login">
        <IconUser />
      </Button>
    );
  }

  const avatarUrl = getUserAvatar(user);
  const fullName = getUserFullName(user);

  return (
    <>
      <DropdownMenu modal={false} >
        <DropdownMenuTrigger asChild>
          <Button
            variant={'icon'}
            className={classNames}
            aria-label="Account"
            title="Account"
          >
            <Avatar className="h-7 w-7 rounded-full border-2 border-navy dark:border-silver">
              <AvatarImage src={avatarUrl} alt={fullName} />
              <AvatarFallback className="rounded-full text-sidebar-foreground">{getInitialNames(fullName)}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align={'end'}
          className={cn(
            'mom-dropdown w-56',
          )}
        >
          <DropdownMenuLabel onClick={() => router.push(URI_ACCOUNT)} className={cn('font-serif cursor-pointer hover:text-navy dark:hover:text-silver')}>Dashboard & Updates</DropdownMenuLabel>

          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link href={URI_ACCOUNT_PROFILE}><User /> <span>Profile</span></Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href={URI_ACCOUNT_PERSONALISATION}><SlidersHorizontal /> <span>Personalisation</span></Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href={URI_ACCOUNT_SAVED_STORIES}><Bookmark /> <span>Saved Stories</span></Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href={URI_ACCOUNT_DISCUSSIONS}><MessagesSquare /> <span>Discussions</span></Link>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          {/* <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link href={URI_ACCOUNT_BILLING}><CreditCard /> <span>Billing</span></Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href={URI_ACCOUNT_SUBSCRIPTIONS}><RefreshCcw /> <span>Subscriptions</span></Link>
          </DropdownMenuItem>
        </DropdownMenuGroup> */}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className={cn('mom-link hover:!text-navy dark:hover:!text-silver')}>
            <LogOut />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}